import { Platform } from "react-native";
import {
  createMMKV as createMMKVFromReactNativeMmkv,
  type Configuration,
  type MMKV,
} from "react-native-mmkv";

// Web server rendering (Expo Router `EXPO_WEB_OUTPUT=server`) runs in Node, where
// `react-native-mmkv`'s web driver backs every read/write with `localStorage`.
// Its `getLocalStorage()` THROWS `"Tried to access storage on the server."` the
// moment any `getString`/`getBoolean`/`set`/… runs without a DOM. Because the app
// tree reads MMKV (device settings, persisted query cache, theme, …) during render,
// that throw aborts server rendering and forces a client-only fallback — the "blank
// white first paint" until the JS bundle hydrates.
//
// There is no persistent storage to reach on the server anyway, so return an
// in-memory MMKV-shaped instance there: every read yields `undefined` (callers
// already treat a missing key as "use the default"), writes go to a Map that is
// discarded when the request ends, and nothing throws. On the client (DOM present)
// and on native this delegates to the real `createMMKV`, so runtime behavior is
// unchanged — the shim only exists for the server render pass.
//
// CRITICAL: gate the DOM sniff behind `Platform.OS === "web"`. React Native aliases
// `globalThis.window` to the global object (so `window` is defined on native) but has
// no DOM, so `window.document?.createElement` is `undefined` there — the bare sniff
// would misclassify EVERY native launch as a server render and hand back the ephemeral
// in-memory Map. That silently made MMKV non-persistent on iOS/Android: device settings
// (e.g. `accountEmail`), the persisted query cache and theme were wiped on every app
// reopen. Only the real web server render (Platform web + no DOM) may use the shim. See
// `suspense-page-boundary.tsx` and `orpc-link-plugins.ts` for the same RN-`window` trap.
const isServerRender =
  Platform.OS === "web" &&
  (globalThis.window === undefined ||
    typeof globalThis.window.document?.createElement !== "function");

function createInMemoryMMKV(config?: Configuration): MMKV {
  const store = new Map<string, string>();
  const listeners = new Set<(key: string) => void>();
  const notify = (key: string) => {
    for (const listener of listeners) {
      listener(key);
    }
  };

  return {
    addOnValueChangedListener: (listener: (key: string) => void) => {
      listeners.add(listener);
      return {
        remove: () => {
          listeners.delete(listener);
        },
      };
    },
    get byteSize() {
      return 0;
    },
    checkContentChanged: () => {},
    clearAll: () => {
      store.clear();
    },
    contains: (key: string) => store.has(key),
    decrypt: () => {},
    dispose: () => {},
    encrypt: () => {},
    equals: () => false,
    getAllKeys: () => [...store.keys()],
    getBoolean: (key: string) => {
      const value = store.get(key);
      return value == null ? undefined : value === "true";
    },
    getBuffer: () => undefined,
    getNumber: (key: string) => {
      const value = store.get(key);
      return value == null ? undefined : Number(value);
    },
    getString: (key: string) => store.get(key),
    id: config?.id ?? "mmkv.default",
    importAllFrom: (other: MMKV) => {
      const keys = other.getAllKeys();
      let imported = 0;
      for (const key of keys) {
        const value = other.getString(key);
        if (value != null) {
          store.set(key, value);
          imported++;
        }
      }
      return imported;
    },
    isEncrypted: false,
    isReadOnly: false,
    get length() {
      return store.size;
    },
    name: "MMKV",
    recrypt: () => {},
    remove: (key: string) => {
      const existed = store.delete(key);
      if (existed) {
        notify(key);
      }
      return existed;
    },
    set: (key: string, value: string | number | boolean | ArrayBuffer) => {
      if (key === "") {
        throw new Error("Cannot set a value for an empty key!");
      }
      // The server render never reads back buffers; store a stable placeholder so
      // `getAllKeys`/`contains` still behave, and `getBuffer` returns `undefined`.
      store.set(key, value instanceof ArrayBuffer ? "" : String(value));
      notify(key);
    },
    get size() {
      return 0;
    },
    trim: () => {},
  } satisfies MMKV;
}

export function createMMKV(config?: Configuration): MMKV {
  if (isServerRender) {
    return createInMemoryMMKV(config);
  }
  return createMMKVFromReactNativeMmkv(config);
}
